import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Settings, Plus, Trash2, Save, GripVertical, ChevronUp, ChevronDown,
  FileText, CheckCircle, XCircle, RefreshCw, Edit2, X, Copy, BookTemplate,
  Eye, AlertTriangle, Loader2
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// Brand colors
const BRAND_BLUE = '#2B4B6A';
const BRAND_ORANGE = '#F58A5A';

const AdminProcessSteps = () => {
  const { user } = useAuth();
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newStep, setNewStep] = useState({
    name: '',
    description: '',
    is_required: true,
    allow_upload_documents: false
  });
  
  // Template states
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [copyingTemplate, setCopyingTemplate] = useState(false);
  const [hasTemplateUpdates, setHasTemplateUpdates] = useState(false);

  const isCompanyAdmin = user?.role === 'company_admin';

  useEffect(() => {
    fetchSteps();
    fetchTemplates();
    if (isCompanyAdmin) {
      checkTemplateUpdates();
    }
  }, [isCompanyAdmin]);
  
  const fetchTemplates = async () => {
    try {
      const res = await api.get('/admin/process-templates');
      setTemplates(res.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };
  
  const checkTemplateUpdates = async () => {
    try {
      const res = await api.get('/process-templates/check-updates');
      setHasTemplateUpdates(res.data.has_updates);
    } catch (error) {
      console.error('Failed to check template updates:', error);
    }
  };
  
  const handlePreviewTemplate = async (templateId) => {
    setLoadingTemplate(true);
    try {
      const res = await api.get(`/admin/process-templates/${templateId}`);
      setTemplatePreview(res.data);
      setSelectedTemplate(templateId);
    } catch (error) {
      toast.error('Failed to load template preview');
    } finally {
      setLoadingTemplate(false);
    }
  };
  
  const handleCopyTemplate = async () => {
    if (!selectedTemplate) return;
    
    setCopyingTemplate(true);
    try {
      const res = await api.post(`/admin/process-templates/${selectedTemplate}/copy`);
      
      // Show success with info about existing quotes
      let message = res.data.message;
      if (res.data.existing_quotes > 0) {
        message += `. Note: ${res.data.existing_quotes} existing quote(s) will keep their original steps.`;
      }
      toast.success(message);
      
      setShowTemplateModal(false);
      setSelectedTemplate(null);
      setTemplatePreview(null);
      fetchSteps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load template');
    } finally {
      setCopyingTemplate(false);
    }
  };

  const fetchSteps = async () => {
    try {
      const res = await api.get('/admin/quote-process/steps');
      setSteps(res.data);
    } catch (error) {
      toast.error('Failed to load process steps');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStep = async () => {
    if (!newStep.name.trim()) {
      toast.error('Step name is required');
      return;
    }

    setSaving(true);
    try {
      const maxOrder = steps.length > 0 ? Math.max(...steps.map(s => s.order_number)) : 0;
      await api.post('/admin/quote-process/steps', {
        ...newStep,
        order_number: maxOrder + 1
      });
      toast.success('Step added successfully');
      setShowAddModal(false);
      setNewStep({ name: '', description: '', is_required: true, allow_upload_documents: false });
      fetchSteps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add step');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStep = async (stepId, updates) => {
    setSaving(true);
    try {
      await api.put(`/admin/quote-process/steps/${stepId}`, updates);
      toast.success('Step updated');
      setEditingStep(null);
      fetchSteps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update step');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStep = async (stepId) => {
    if (!window.confirm('Are you sure you want to delete this step? This cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/admin/quote-process/steps/${stepId}`);
      toast.success('Step deleted');
      fetchSteps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete step');
    }
  };

  const handleMoveStep = async (index, direction) => {
    const newSteps = [...steps];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= steps.length) return;
    
    // Swap order numbers
    const temp = newSteps[index].order_number;
    newSteps[index].order_number = newSteps[newIndex].order_number;
    newSteps[newIndex].order_number = temp;
    
    // Swap positions
    [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
    
    setSteps(newSteps);
    
    try {
      await api.put('/admin/quote-process/steps/reorder', {
        items: newSteps.map((s, i) => ({ id: s.id, order_number: i + 1 }))
      });
      fetchSteps();
    } catch (error) {
      toast.error('Failed to reorder steps');
      fetchSteps();
    }
  };

  const handleToggleRequired = async (step) => {
    await handleUpdateStep(step.id, { is_required: !step.is_required });
  };

  const handleToggleUploads = async (step) => {
    await handleUpdateStep(step.id, { allow_upload_documents: !step.allow_upload_documents });
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Template Update Alert */}
        {hasTemplateUpdates && isCompanyAdmin && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-blue-600" />
              <span className="text-blue-800">Template updates are available. Preview and update your process steps.</span>
            </div>
            <button
              onClick={() => setShowTemplateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
            >
              View Updates
            </button>
          </div>
        )}
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Quote Process Steps</h1>
            <p className="text-gray-500 text-sm mt-1">Manage the buying process steps. Users will progress through these sequentially.</p>
          </div>
          <div className="flex items-center gap-3">
            {templates.length > 0 && (
              <button
                onClick={() => setShowTemplateModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition"
                data-testid="load-template-btn"
              >
                <BookTemplate className="w-4 h-4" />
                Load Template
              </button>
            )}
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 text-white rounded-xl transition font-medium text-sm"
              style={{ backgroundColor: BRAND_BLUE }}
              data-testid="add-step-btn"
            >
              <Plus className="w-5 h-5" />
              Add Step
            </button>
          </div>
        </div>

        {/* Steps List */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/80">
            <div className="grid grid-cols-12 gap-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              <div className="col-span-1">Order</div>
              <div className="col-span-4">Step Name</div>
              <div className="col-span-2 text-center">Required</div>
              <div className="col-span-2 text-center">Allow Uploads</div>
              <div className="col-span-1 text-center">Questions</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
          </div>
          
          {steps.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No process steps configured yet.</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-4 text-sm font-medium hover:underline"
                style={{ color: BRAND_BLUE }}
              >
                Add your first step
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {steps.map((step, index) => (
                <div 
                  key={step.id} 
                  className="px-6 py-4 hover:bg-gray-50/50 transition-colors"
                  data-testid={`step-row-${step.id}`}
                >
                  {editingStep === step.id ? (
                    // Edit mode
                    <EditStepRow 
                      step={step} 
                      onSave={(updates) => handleUpdateStep(step.id, updates)}
                      onCancel={() => setEditingStep(null)}
                      saving={saving}
                    />
                  ) : (
                    // View mode
                    <div className="grid grid-cols-12 gap-4 items-center">
                      {/* Order with move buttons */}
                      <div className="col-span-1 flex items-center gap-2">
                        <div className="flex flex-col">
                          <button
                            onClick={() => handleMoveStep(index, 'up')}
                            disabled={index === 0}
                            className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleMoveStep(index, 'down')}
                            disabled={index === steps.length - 1}
                            className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                        <span className="text-sm font-medium text-gray-600">{step.order_number}</span>
                      </div>
                      
                      {/* Name & Description */}
                      <div className="col-span-4">
                        <p className="font-medium text-gray-900">{step.name}</p>
                        {step.description && (
                          <p className="text-xs text-gray-500 mt-0.5 truncate">{step.description}</p>
                        )}
                      </div>
                      
                      {/* Required Toggle */}
                      <div className="col-span-2 flex justify-center">
                        <button
                          onClick={() => handleToggleRequired(step)}
                          className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition ${
                            step.is_required 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-500'
                          }`}
                          data-testid={`toggle-required-${step.id}`}
                        >
                          {step.is_required ? (
                            <>
                              <CheckCircle className="w-3.5 h-3.5" />
                              Required
                            </>
                          ) : (
                            <>
                              <XCircle className="w-3.5 h-3.5" />
                              Optional
                            </>
                          )}
                        </button>
                      </div>
                      
                      {/* Allow Uploads Toggle */}
                      <div className="col-span-2 flex justify-center">
                        <button
                          onClick={() => handleToggleUploads(step)}
                          className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition ${
                            step.allow_upload_documents 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-gray-100 text-gray-500'
                          }`}
                          data-testid={`toggle-uploads-${step.id}`}
                        >
                          {step.allow_upload_documents ? (
                            <>
                              <CheckCircle className="w-3.5 h-3.5" />
                              Enabled
                            </>
                          ) : (
                            <>
                              <XCircle className="w-3.5 h-3.5" />
                              Disabled
                            </>
                          )}
                        </button>
                      </div>
                      
                      {/* Question Count */}
                      <div className="col-span-1 text-center">
                        <span className="text-sm text-gray-600">{step.question_count || 0}</span>
                      </div>
                      
                      {/* Actions */}
                      <div className="col-span-2 flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditingStep(step.id)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteStep(step.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Step Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Add New Step</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Step Name *</label>
                  <input
                    type="text"
                    value={newStep.name}
                    onChange={(e) => setNewStep({...newStep, name: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                    placeholder="e.g., Document Verification"
                    data-testid="new-step-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={newStep.description}
                    onChange={(e) => setNewStep({...newStep, description: e.target.value})}
                    rows={2}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                    placeholder="Brief description of this step"
                    data-testid="new-step-description"
                  />
                </div>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newStep.is_required}
                      onChange={(e) => setNewStep({...newStep, is_required: e.target.checked})}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Required Step</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newStep.allow_upload_documents}
                      onChange={(e) => setNewStep({...newStep, allow_upload_documents: e.target.checked})}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Allow Uploads</span>
                  </label>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddStep}
                  disabled={saving || !newStep.name.trim()}
                  className="flex items-center gap-2 px-5 py-2 text-white rounded-xl text-sm font-medium disabled:opacity-50"
                  style={{ backgroundColor: BRAND_BLUE }}
                  data-testid="save-new-step"
                >
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Add Step
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Template Selection Modal */}
        {showTemplateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Load Process Template</h3>
                  <p className="text-sm text-gray-500">Select a template to use for new quote requests</p>
                </div>
                <button
                  onClick={() => {
                    setShowTemplateModal(false);
                    setSelectedTemplate(null);
                    setTemplatePreview(null);
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="flex-1 overflow-hidden flex">
                {/* Template List */}
                <div className="w-1/3 border-r border-gray-100 overflow-y-auto p-4">
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Available Templates</h4>
                  <div className="space-y-2">
                    {templates.map(template => (
                      <button
                        key={template.id}
                        onClick={() => handlePreviewTemplate(template.id)}
                        className={`w-full text-left p-3 rounded-xl border transition ${
                          selectedTemplate === template.id
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <BookTemplate className={`w-4 h-4 ${selectedTemplate === template.id ? 'text-orange-600' : 'text-gray-400'}`} />
                          <span className="font-medium text-gray-900 text-sm">{template.name}</span>
                        </div>
                        <p className="text-xs text-gray-500">
                          {template.step_count} steps • {template.question_count} questions
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Preview Panel */}
                <div className="w-2/3 overflow-y-auto p-6">
                  {loadingTemplate ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
                    </div>
                  ) : templatePreview ? (
                    <div>
                      <h4 className="text-lg font-semibold mb-1">{templatePreview.name}</h4>
                      {templatePreview.description && (
                        <p className="text-sm text-gray-500 mb-4">{templatePreview.description}</p>
                      )}
                      
                      <div className="space-y-3">
                        {templatePreview.steps?.map((step, idx) => (
                          <div key={step.id} className="border border-gray-100 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="w-6 h-6 bg-orange-100 rounded text-orange-600 text-xs font-medium flex items-center justify-center">
                                {step.order_number}
                              </span>
                              <span className="font-medium">{step.name}</span>
                              {step.is_required && (
                                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Required</span>
                              )}
                            </div>
                            {step.questions?.length > 0 && (
                              <div className="ml-8 space-y-1">
                                {step.questions.map((q, qIdx) => (
                                  <div key={q.id} className="text-sm text-gray-600 flex items-start gap-2">
                                    <span className="text-gray-400">{qIdx + 1}.</span>
                                    <span>{q.question_text}</span>
                                    <span className="text-xs text-gray-400">({q.field_type})</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                      <Eye className="w-12 h-12 mb-3" />
                      <p>Select a template to preview</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>Important:</strong> Loading a new template will only affect <strong>NEW quotes</strong>. 
                      Any existing open or closed quotes will continue using their original process steps.
                    </span>
                  </p>
                </div>
                <div className="flex justify-between items-center">
                  <button
                    onClick={() => {
                      setShowTemplateModal(false);
                      setSelectedTemplate(null);
                      setTemplatePreview(null);
                    }}
                    className="px-5 py-2 text-gray-600 hover:bg-gray-100 rounded-xl text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCopyTemplate}
                    disabled={!selectedTemplate || copyingTemplate}
                    className="flex items-center gap-2 px-5 py-2 bg-orange-600 text-white rounded-xl text-sm font-medium disabled:opacity-50 hover:bg-orange-700"
                    data-testid="copy-template-btn"
                  >
                    {copyingTemplate ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                    Load Template
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

// Edit Step Row Component
const EditStepRow = ({ step, onSave, onCancel, saving }) => {
  const [editData, setEditData] = useState({
    name: step.name,
    description: step.description || ''
  });

  return (
    <div className="grid grid-cols-12 gap-4 items-center">
      <div className="col-span-1">
        <span className="text-sm font-medium text-gray-600">{step.order_number}</span>
      </div>
      <div className="col-span-7">
        <input
          type="text"
          value={editData.name}
          onChange={(e) => setEditData({...editData, name: e.target.value})}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 text-sm mb-2"
          placeholder="Step name"
        />
        <input
          type="text"
          value={editData.description}
          onChange={(e) => setEditData({...editData, description: e.target.value})}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 text-sm"
          placeholder="Description (optional)"
        />
      </div>
      <div className="col-span-4 flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium"
        >
          Cancel
        </button>
        <button
          onClick={() => onSave(editData)}
          disabled={saving || !editData.name.trim()}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          style={{ backgroundColor: BRAND_BLUE }}
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
      </div>
    </div>
  );
};

export default AdminProcessSteps;
